import { useSignUp, isClerkAPIResponseError } from "@clerk/clerk-react"
import { Logo } from "../../../assets/icons"
import {
	Center,
	Stack,
	Text,
	Group,
	Divider,
	rem,
	createStyles,
	Button,
	Box,
	MediaQuery,
	Flex,
} from "@mantine/core"
import { useDocumentTitle, useTimeout } from "@mantine/hooks"
import CustomTextInput from "../../../Components/TextInput/TextInput"
import SocialIcon from "../Components/SocialIcon"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, SubmitHandler } from "react-hook-form"
import CustomPasswordInput from "../../../Components/PasswordInput/PasswordInput"
import { IconMailFilled, IconX } from "@tabler/icons-react"
import { Link, Navigate, useNavigate } from "react-router-dom"
import useOauth from "../../../hooks/useOauth"
import { useEffect, useState } from "react"
import OTP from "../OTP/OTP"
import { notifications } from "@mantine/notifications"

const registerFormSchema = z.object({
	email: z
		.string({
			required_error: "email is required",
			invalid_type_error: "Invalid email format",
		})
		.email({ message: "Invalid email format" }),
	password: z
		.string()
		.min(8, { message: "Password must have at least 8 characters" })
		.regex(/[$&+,:;=?@#|'<>.^*()%!-]/, {
			message: "Password must contain one special character",
		})
		.regex(/[A-Z]/, {
			message: "Password must contain one uppercase character",
		})
		.regex(/[0-9]/, {
			message: "Password must contain one number",
		})
		.regex(/[a-z]/, {
			message: "Password must contain one lowercase character",
		}),
})
type registerFormType = z.infer<typeof registerFormSchema>
const useStyles = createStyles(theme => ({
	label: {
		color: theme.colors["refactor"][5],
		margin: "0 auto !important",
		"::before": {
			borderTop: `1px solid ${theme.colors["refactor"][5]}`,
			height: rem(1),
		},
		"::after": {
			borderTop: `1px solid ${theme.colors["refactor"][5]}`,
			height: rem(1),
		},
	},

	signInLink: {
		textDecoration: "none",
		display: "inline",
		fontWeight: 600,
		fontSize: "inherit",
		linHeight: "inherit",
		letterSpacing: "inherit",
		fontVariantNumeric: "inherit",
		":visited": {
			color: theme.colors["brand"][2],
		},
	},
	serviceLink: {
		display: "inline",
		fontWeight: 400,
		fontSize: "inherit",
		linHeight: "inherit",
		letterSpacing: "inherit",
		fontVariantNumeric: "inherit",
		color: theme.colors["refactor"][0],
	},
	password: {
		marginTop: rem(16),
	},
}))
export default function SignUp() {
	const { classes, cx } = useStyles()
	const { signUp, isLoaded, setActive } = useSignUp()
	const navigate = useNavigate()
	const [loading, setLoading] = useState(false)
	const [emailError, setEmailError] = useState<string | undefined>(undefined)
	const [resendDisabled, setResendDisabled] = useState(false)
	useDocumentTitle("Register")
	const ticketParam = "__clerk_ticket"
	const statusParam = "__clerk_status"
	const emailParam = "email"
	const ticket = new URL(window.location.href).searchParams.get(ticketParam)
	const status = new URL(window.location.href).searchParams.get(statusParam)
	const emailP = new URL(window.location.href).searchParams.get(emailParam)

	const [pendingVerification, setPendingVerification] = useState(false)
	const { start, clear } = useTimeout(() => setResendDisabled(false), 30000)
	const { signInWithGoogle, signInWithGithub, signInWithGitlab } = useOauth()
	const { control, handleSubmit, reset, watch, formState, getValues } =
		useForm<registerFormType>({
			resolver: zodResolver(registerFormSchema),
			defaultValues: {
				email: emailP ? emailP : "",
				password: "",
			},
		})

	useEffect(() => {
		return () => {
			clear()
		}
	}, [])
	const submit: SubmitHandler<registerFormType> = async values => {
		setLoading(true)
		if (!isLoaded) {
			setLoading(false)
			return
		}
		setEmailError(undefined)
		try {
			if (!ticket) {
				const create = await signUp.create({
					emailAddress: values.email,
					password: values.password,
				})

				const sendMail = await signUp.prepareEmailAddressVerification({
					strategy: "email_code",
				})
				setPendingVerification(true)
			} else {
				const response = await signUp.create({
					strategy: "ticket",
					password: values.password,
					emailAddress: values.email,
					ticket,
				})

				if (response.status === "complete") {
					await setActive({ session: response.createdSessionId })
					navigate("/")
				}
			}
		} catch (error) {
			if (isClerkAPIResponseError(error)) {
				if (
					error.errors[0].longMessage ===
					"That email address is taken. Please try another."
				) {
					setEmailError(error.errors[0].longMessage)
				} else {
					notifications.show({
						withCloseButton: true,
						color: "danger.0",
						title: "Error",
						icon: <IconX />,
						autoClose: 5000,
						message: error.errors[0].longMessage,
					})
				}
			} else {
				notifications.show({
					withCloseButton: true,
					color: "danger.0",
					title: "Error",
					icon: <IconX />,
					autoClose: 5000,
					message: "Something went wrong",
				})
			}
			console.log(error)
		}

		reset()
		setLoading(false)
	}

	const resendMail = async () => {
		setLoading(true)
		if (!isLoaded) {
			setLoading(false)
			return
		}
		try {
			const sendMail = await signUp.prepareEmailAddressVerification({
				strategy: "email_code",
			})
		} catch (error) {
			if (isClerkAPIResponseError(error)) {
				notifications.show({
					withCloseButton: true,
					color: "danger.0",
					title: "Error",
					icon: <IconX />,
					autoClose: 5000,
					message: error.errors[0].longMessage,
				})
			} else {
				notifications.show({
					withCloseButton: true,
					color: "danger.0",
					title: "Error",
					icon: <IconX />,
					autoClose: 5000,
					message: "Something went wrong",
				})
			}
		}
		setResendDisabled(true)
		start()
		setLoading(false)
	}

	if (pendingVerification) {
		return (
			<OTP
				email={getValues("email")}
				resend={resendMail}
				resendSuccess={resendDisabled}
				setPendingVerification={() => {
					setPendingVerification(false)
				}}
			/>
		)
	}

	if (status && status === "sign_in" && ticket && emailP) {
		return (
			<Navigate
				to={`/login?email=${encodeURIComponent(
					emailP
				)}&${statusParam}=${status}&${ticketParam}=${ticket}`}
			/>
		)
	}
	return (
		<MediaQuery
			query='(min-width: 75em)'
			styles={{
				width: rem(360),
			}}
		>
			<Flex
				sx={{
					width: `max(${rem(360)}, 45vw)`,
				}}
			>
				<Stack
					justify='center'
					sx={{
						gap: rem(0),
					}}
				>
					<Box mb={!!ticket ? rem(48) : rem(75)}>
						<Logo />
					</Box>
					<Stack sx={{ gap: rem(8) }}>
						<Text
							component='h1'
							sx={theme => ({
								fontSize: rem(36),
								fontWeight: 600,
								lineHeight: rem(40),
								letterSpacing: rem(-0.2),
								color: theme.colors[theme.primaryColor][7],
							})}
							mt={0}
							mb={0}
						>
							Get Started
						</Text>
						<Text
							component='h2'
							mt={0}
							mb={0}
							sx={theme => ({
								lineHeight: rem(24),
								fontSize: rem(16),
								letterSpacing: rem(-0.2),
								color: theme.colors["refactor"][4],
								fontWeight: 400,
							})}
						>
							Enhance the performance, security, and availability of your
							API infrastructure.
						</Text>
					</Stack>

					{!!ticket ? null : (
						<>
							<Group
								spacing={rem(16)}
								sx={{
									marginTop: rem(40),
								}}
							>
								<SocialIcon
									type='github'
									onClick={() => {
										if (signInWithGithub) {
											if (loading) {
												return
											}
											setLoading(true)
											signInWithGithub().then(r => setLoading(false))
										}
									}}
								/>
								<SocialIcon
									type='gitlab'
									onClick={() => {
										if (signInWithGitlab) {
											if (loading) {
												return
											}
											setLoading(true)
											signInWithGitlab().then(r => setLoading(false))
										}
									}}
								/>
								<SocialIcon
									type='google'
									onClick={() => {
										if (signInWithGoogle) {
											if (loading) {
												return
											}
											setLoading(true)
											signInWithGoogle().then(r => setLoading(false))
										}
									}}
								/>
							</Group>
							<Center w='100%'>
								<Divider
									my='xs'
									label='or'
									classNames={{ label: classes.label }}
									labelPosition='center'
									w={rem("75%")}
								/>
							</Center>
						</>
					)}
					<Box
						component='form'
						sx={{
							alignItems: "center",
							justifyContent: "center",
							display: "flex",
							flexDirection: "column",
							gap: rem(16),
						}}
						w={"100%"}
						onSubmit={handleSubmit(submit)}
						mt={!!ticket ? rem(24) : 0}
					>
						<CustomTextInput
							name='email'
							placeholder='Email address'
							label='Email address'
							control={control}
							disabled={loading}
							value={watch("email")}
							errorMessage={
								formState.errors.email?.message || emailError
							}
						/>
						<CustomPasswordInput
							name='password'
							control={control}
							placeholder='Password'
							label='Password'
							value={watch("password")}
							className={cx({
								[classes.password]:
									!!formState.errors.email || emailError,
							})}
							disabled={loading}
							errorMessage={formState.errors.password?.message}
						/>
						<Button
							variant='filled'
							leftIcon={<IconMailFilled />}
							type='submit'
							radius='md'
							fullWidth
							sx={theme => ({
								padding: rem(16),
								height: rem(52),
								":disabled": {
									background: theme.colors["refactor"][5],
									color: theme.colors["refactor"][4],
								},
							})}
							disabled={loading}
							loading={loading}
							className={cx({
								[classes.password]: !!formState.errors.password,
							})}
						>
							Continue with Email
						</Button>
					</Box>
					<Flex
						mt={rem(16)}
						direction='column'
						gap={rem(24)}
						align='center'
					>
						{!!ticket ? null : (
							<Text
								align='center'
								sx={theme => ({
									color: theme.colors["refactor"][0],
									fontVariantNumeric: "lining-nums  proportional-nums",
									fontSize: rem(14),
									lineHeight: rem(16),
									letterSpacing: -0.2,
									display: "inline-block",

									borderRadius: rem(6),
									background: theme.white,
									border: ` 1px solid ${theme.colors["refactor"][7]}`,
									padding: `${rem(10)} ${rem(16)}`,
									width: "fit-content",
								})}
							>
								Already have an account?{" "}
								<Link
									to='/login'
									className={classes.signInLink}
								>
									Sign In
								</Link>
							</Text>
						)}
						<Text
							align='center'
							sx={theme => ({
								letterSpacing: -0.2,
								lineHeight: rem(20),
								fontSize: rem(14),
								color: theme.colors["refactor"][4],
							})}
						>
							By signing up you agree to our{" "}
							<Link
								to='https://resilis.io/docs/terms-of-service'
								className={classes.serviceLink}
								target='_blank'
							>
								Terms of Service
							</Link>{" "}
							and our{" "}
							<Link
								to='https://resilis.io/docs/privacy-policy'
								className={classes.serviceLink}
								target='_blank'
							>
								Privacy Policy
							</Link>
							.
						</Text>
					</Flex>
				</Stack>
			</Flex>
		</MediaQuery>
	)
}
